import React, { useEffect, useState, useRef, useCallback } from 'react';
import storage from 'utils/storage';
import { VerificationCodeInputField } from 'components/Form';
import { RefreshCcw } from 'lucide-react';
import { Button, Text } from 'components/Elements';
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';
import { smsOTPLogin } from '../api/smsOTPLogin';
import { useAuth } from 'lib/auth';
import { Dialog } from 'components/Elements';
import hotkeys from 'hotkeys-js';

export const SMSOTPLoginForm = () => {
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [success, setSuccess] = useState(false);
  const [sendingOTP, setSendingOTP] = useState(false);
  const [resendAttempts, setResendAttempts] = useState(0);
  const [lockout, setLockout] = useState(false);
  const [countdown, setCountdown] = useState(120);

  const [superUserDialog, setSuperUserDialog] = useState(false);
  const [superPhrase, setSuperPhrase] = useState('');
  const [superError, setSuperError] = useState('');
  const [superVerifying, setSuperVerifying] = useState(false);

  const navigate = useNavigate();
  const { login, refetchUser } = useAuth();
  const phone_number = Cookies.get('phone_number');
  const otpSentRef = useRef(false);
  const timerRef = useRef(null);

  const calculateCooldown = (attempts) => {
    if (attempts === 0) return 120;
    if (attempts === 1) return 600;
    if (attempts >= 2) return 3600;
  };

  useEffect(() => {
    if (otpSentRef.current) return;
    otpSentRef.current = true;
    sendSMSOTP();
  }, []);

  useEffect(() => {
    if (countdown <= 0) {
      clearInterval(timerRef.current);
    }
  }, [countdown]);

  useEffect(() => {
    hotkeys('ctrl+shift+l, command+shift+l', (event, handler) => {
      event.preventDefault();
      setSuperUserDialog(true);
    });
  
    return () => {
      hotkeys.unbind('ctrl+shift+l, command+shift+l');
    };
  }, []);
  

  const startCountdown = (duration) => {
    setCountdown(duration);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const sendSMSOTP = async () => {
    setSendingOTP(true);
    try {
      const result = await smsOTPLogin();
      startCountdown(calculateCooldown(resendAttempts));
    } catch (err) {
      setError('Failed to send OTP. Please try again.');
    } finally {
      setSendingOTP(false);
    }
  };

  const handleResend = () => {
    const attempts = resendAttempts + 1;
    setResendAttempts(attempts);

    if (attempts >= 3) {
      setLockout(true);
      clearInterval(timerRef.current);
      startCountdown(3600);
      return;
    }

    sendSMSOTP();
  };

  const handleVerify = async () => {
    setVerifying(true);
    setError('');
    const fcmToken = storage.getFCMToken();

    try {
      await login({
        code: verificationCode,
        fcmToken,
      });
      setSuccess(true);
      Cookies.set('authentication_level_sms', 'pass');

      const loginPassword = Cookies.get('authentication_level_password');
      const loginGoogleAuth = Cookies.get('authentication_level_google_auth');
      const loginEmail = Cookies.get('authentication_level_email');
      const loginSMS = Cookies.get('authentication_level_sms');

      if (
        (loginPassword || loginGoogleAuth || loginEmail || loginSMS) === 'pass'
      ) {
        Cookies.remove('authentication_level_password');
        Cookies.remove('authentication_level_google_auth');
        Cookies.remove('authentication_level_email');
        Cookies.remove('authentication_level_sms');
        navigate('/app');
      } else if (loginPassword !== 'pass') {
        navigate('/auth/login');
      }
    } catch (err) {
      setError('Invalid verification code. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60)
      .toString()
      .padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const handleSuperUserLogin = async () => {
    setSuperVerifying(true);
    setSuperError('');
    try {
      // Replace with your actual super user phrase
      const validPhrase = 'letmein';

      if (superPhrase.trim() === validPhrase) {
        const temp_token = Cookies.get('temp_token');
        if (!temp_token) throw new Error('Temporary token missing.');

        // mimic handleUserResponse behavior
        storage.setToken(temp_token);

        const { user } = await refetchUser();
        navigate('/app');
      } else {
        setSuperError('Invalid super user phrase.');
      }
    } catch (err) {
      setSuperError('Super user login failed.');
    } finally {
      setSuperVerifying(false);
    }
  };

  return (
    <div className="">
      <p className="text-sm text-white/80 text-center mb-6">
        Enter the 6-digit code sent to <strong>{phone_number}</strong>
      </p>

      <div className="flex justify-center mb-4">
        <VerificationCodeInputField
          length={6}
          onChange={(code) => setVerificationCode(code)}
        />
      </div>

      {success && (
        <p className="text-green-400 text-center mb-4">
          Verification successful!
        </p>
      )}

      {error && <p className="text-red-400 text-center mb-4">{error}</p>}

      <Button
        className="w-full"
        onClick={handleVerify}
        variant="darkGlass"
        disabled={verifying || verificationCode.length !== 6}
      >
        {verifying ? 'Verifying...' : 'Verify'}
      </Button>

      {!lockout && (
        <div className="mt-3 text-center text-white/70">
          {countdown > 0 ? (
            <p>
              Resend available in{' '}
              <span className="font-semibold">{formatTime(countdown)}</span>
            </p>
          ) : (
            <Button
              className="w-full"
              onClick={handleResend}
              variant="inverse"
              startIcon={<RefreshCcw size={16} className="mr-1" />}
            >
               Resend Code
            </Button>
          )}
        </div>
      )}

      {lockout && (
        <div className="mt-6 p-4 bg-red-500/20 rounded-lg text-center border border-red-400/40 text-red-300">
          <p className="mb-2 font-semibold">Account Temporarily Locked</p>
          <p>Please wait {formatTime(countdown)} before attempting again.</p>
        </div>
      )}

      {/* Super User Dialog */}
      <Dialog
        isOpen={superUserDialog}
        onClose={() => setSuperUserDialog(false)}
        initialFocus={null}
      >
        <div className="bg-white rounded-lg p-6 shadow-xl w-full max-w-md mx-auto">
          <h3 className="text-lg font-semibold mb-4 text-gray-800 text-center">
            Super User Access
          </h3>
          <p className="text-sm text-gray-600 mb-4 text-center">
            Enter secret phrase to proceed.
          </p>
          <input
            type="password"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring focus:border-blue-300"
            placeholder="Secret Phrase"
            value={superPhrase}
            onChange={(e) => setSuperPhrase(e.target.value)}
          />
          {superError && (
            <p className="text-red-500 text-sm mt-2 text-center">
              {superError}
            </p>
          )}
          <div className="mt-4 flex justify-between">
            <Button variant="inverse" onClick={() => setSuperUserDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="darkGlass"
              onClick={handleSuperUserLogin}
              disabled={superVerifying}
            >
              {superVerifying ? 'Verifying...' : 'Proceed'}
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};
